import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { RecommendationSpeciality } from '@dentalux/ui-library-core/type';
import { Divider, Button, Accordion, Typography, useTheme } from '@mui/material';
import { RecommendationFrequencyUnit, SingularRecommendationFrequencyUnit } from 'src/@types/Recommendation';
import { CalendarIcon } from 'src/components/icons/Calendar';
import CollapseArrow from 'src/components/icons/CollapseArrow';
import ExpandArrow from 'src/components/icons/ExpandArrow';
import RecommendationStethoscope from 'src/components/icons/RecommendationStethoscope';
import RecommendationTooth from 'src/components/icons/RecommendationTooth';
import Loader from 'src/components/Loader/Loader';
import SwipeableDrawer from 'src/components/SwipeableDrawer';
import { DataTestIds } from 'src/config/dataTestIds';
import { formatShort } from 'src/helpers/date';
import { useBookRecommendation } from 'src/hooks/useBookRecommendation';
import useDateFnsConfig from 'src/hooks/useDateFnsConfig';
import useIsDesktop from 'src/hooks/useIsDesktop';
import useIsMobile from 'src/hooks/useIsMobile';
import { Trait } from '../Trait/Trait';
import * as S from './Recommendation.styles';
const singularFrequency = {
  [RecommendationFrequencyUnit.Hours]: SingularRecommendationFrequencyUnit.Hour,
  [RecommendationFrequencyUnit.Days]: SingularRecommendationFrequencyUnit.Day,
  [RecommendationFrequencyUnit.HalfDays]: SingularRecommendationFrequencyUnit.HalfDay,
  [RecommendationFrequencyUnit.Weeks]: SingularRecommendationFrequencyUnit.Week,
  [RecommendationFrequencyUnit.Months]: SingularRecommendationFrequencyUnit.Month,
  [RecommendationFrequencyUnit.Years]: SingularRecommendationFrequencyUnit.Year,
  [RecommendationFrequencyUnit.Decades]: SingularRecommendationFrequencyUnit.Decade
};
export const Recommendation = ({
  data,
  onClose,
  width
}) => {
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const colors = theme.palette;
  const [showDetails, setShowDetails] = useState(false);
  const [bookLoading, setBookLoading] = useState(false);
  const config = useDateFnsConfig();
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const isOther = data.speciality !== RecommendationSpeciality.DENTAL;
  const Title = ({
    inDrawer
  }) => /*#__PURE__*/React.createElement(React.Fragment, null, (inDrawer || !isMobile) && /*#__PURE__*/React.createElement(S.Frame, {
    isOther: isOther
  }, isOther ? /*#__PURE__*/React.createElement(RecommendationStethoscope, null) : /*#__PURE__*/React.createElement(RecommendationTooth, null)), /*#__PURE__*/React.createElement(S.Title, {
    inDrawer: inDrawer,
    variant: inDrawer ? 'DM Serif H4' : isDesktop ? 'Lato H5 Bold' : 'Lato Emphasized 1'
  }, inDrawer && `${t('b2c.recommendations.details')} -`, " ", t(data.titleTranslationKey)));
  const handleBook = useBookRecommendation({
    data
  });
  const buttonColor = isOther ? 'purple' : 'blue';
  const arrowProps = {
    fill: isDesktop ? colors.grey[800] : buttonColor
  };
  const lastPerformed = data.treatmentBundles.map(el => el.lastTimePersonHadThatTreatment).filter(el => el).sort((a, b) => Number(new Date(b)) - Number(new Date(a)));
  const bookable = data?.treatmentBundles?.length > 0;
  const onBookClick = e => {
    setBookLoading(true);
    handleBook(e);
  };
  return /*#__PURE__*/React.createElement(S.Root, {
    Title: /*#__PURE__*/React.createElement(Title, null),
    width: width,
    onClose: onClose,
    onClick: isDesktop ? undefined : () => setShowDetails(!showDetails),
    dataTestId: DataTestIds.RecommendationCard,
    "data-speciality": data.speciality
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(S.Importance, {
    variant: isDesktop ? 'Lato Emphasized 2' : 'Lato Caption Bold'
  }, t('b2c.recommendations.importance')), /*#__PURE__*/React.createElement(S.Emphasize, {
    isOther: isOther
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: isDesktop ? 'Lato Body 2' : 'Lato Caption'
  }, t(data.shortDescriptionTranslationKey))), /*#__PURE__*/React.createElement(S.Introduction, null, isOther && /*#__PURE__*/React.createElement(Trait, {
    value: t(`b2c.recommendations.traits.specialist.${data.speciality}`),
    label: 'b2c.recommendations.traits.speciality'
  }), lastPerformed.length > 0 && /*#__PURE__*/React.createElement(Trait, {
    value: formatShort(new Date(lastPerformed[0]), config),
    label: 'b2c.recommendations.traits.lastVisit'
  }), data?.frequency && /*#__PURE__*/React.createElement(Trait, {
    value: t('b2c.recommendations.traits.rhythm', {
      unit: t(`b2c.recommendations.traits.frequency${data.frequency.count === 1 ? singularFrequency[data.frequency.unit] : data.frequency.unit}`),
      count: data.frequency.count
    }),
    label: 'b2c.recommendations.traits.frequency'
  }), data.costTranslationKey && t(data.costTranslationKey) !== '-' && /*#__PURE__*/React.createElement(Trait, {
    value: t(data.costTranslationKey),
    label: 'b2c.recommendations.traits.cost'
  }))), /*#__PURE__*/React.createElement(S.Controls, null, bookable && /*#__PURE__*/React.createElement(Button, {
    size: "sm",
    onClick: onBookClick,
    startIcon: bookLoading ? /*#__PURE__*/React.createElement(Loader, {
      center: false,
      size: "xs"
    }) : /*#__PURE__*/React.createElement(CalendarIcon, null),
    color: buttonColor,
    "data-testid": DataTestIds.DashboardBookRecommendation,
    variant: "bordered",
    fullWidth: true,
    disabled: bookLoading
  }, t('b2c.recommendations.book')), isDesktop && /*#__PURE__*/React.createElement(Divider, null), isDesktop && /*#__PURE__*/React.createElement(Button, {
    size: "md",
    endIcon: isDesktop ? showDetails ? /*#__PURE__*/React.createElement(CollapseArrow, arrowProps) : /*#__PURE__*/React.createElement(ExpandArrow, arrowProps) : undefined,
    onClick: () => setShowDetails(!showDetails),
    variant: isDesktop ? 'borderless' : 'shadowed',
    color: isDesktop ? 'grey' : buttonColor
  }, t('b2c.recommendations.details'))), isDesktop ? /*#__PURE__*/React.createElement(Accordion, {
    disableGutters: true,
    expanded: isDesktop && showDetails
  }, /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(S.Details, {
    dangerouslySetInnerHTML: {
      __html: t(data.longDescriptionTranslationKey)
    }
  })) : /*#__PURE__*/React.createElement(SwipeableDrawer, {
    onClose: () => setShowDetails(false),
    closeDrawer: () => setShowDetails(false),
    open: showDetails,
    header: /*#__PURE__*/React.createElement(S.Header, null, /*#__PURE__*/React.createElement(Title, null)),
    action: bookable && /*#__PURE__*/React.createElement(Button, {
      color: buttonColor,
      onClick: onBookClick,
      fullWidth: true,
      size: "lg",
      "data-testid": DataTestIds.DashboardBookRecommendationFromDetils,
      disabled: bookLoading,
      startIcon: bookLoading && /*#__PURE__*/React.createElement(Loader, {
        center: false,
        size: "xs"
      })
    }, t('b2c.recommendations.book'))
  }, /*#__PURE__*/React.createElement(S.Details, {
    inDrawer: true,
    dangerouslySetInnerHTML: {
      __html: t(data.longDescriptionTranslationKey)
    }
  })));
};